"use client"

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from "react"

/**
 * Generic "extra invocation context" mechanism for chat.
 *
 * Some agents need to attach agent-specific, request-scoped fields to the
 * runtime invocation body.
 *
 * A contributor returns the fields to include for the next message send, or an
 * empty object to contribute nothing. Contributors are read at send time, so
 * they always reflect the current selection/state.
 */

/** A function that returns extra invocation-body fields to include on a send. */
export type InvocationContextContributor = () => Record<string, unknown>

/**
 * A listener invoked (fire-and-forget) once the chat session's backend row has
 * just been persisted (created on the first message, or confirmed to already
 * exist). Lets agent-specific providers run a per-session side effect that
 * requires the session row to exist — e.g. writing per-session state keyed by
 * the session id — without the generic send path knowing what that side effect
 * is. Fired once per session (on the not-persisted → persisted transition), not
 * on every message. Listeners must not throw; they own their own error handling.
 */
export type PersistListener = () => void

/** Public API of the invocation-context registry. */
interface InvocationContextValue {
  /**
   * Register a contributor. Returns an unregister function to call on cleanup.
   *
   * @param contributor - Returns the extra body fields to include on each send.
   * @returns A function that removes this contributor when invoked.
   */
  register: (contributor: InvocationContextContributor) => () => void
  /**
   * Collect and merge the current extra body fields from all contributors.
   *
   * Later contributors override earlier ones on key collision. Returns an empty
   * object when there are no contributors or none contribute anything.
   *
   * @returns The merged extra invocation-body fields.
   */
  collect: () => Record<string, unknown>
  /**
   * Register a persist listener. Returns an unregister function for cleanup.
   *
   * @param listener - Invoked once when the session row is first persisted.
   * @returns A function that removes this listener when invoked.
   */
  registerPersistListener: (listener: PersistListener) => () => void
  /**
   * Notify all registered persist listeners that the session row has just been
   * persisted. Called by the send path once per session; individual listeners
   * decide what (if anything) to do. Never throws.
   */
  notifyPersisted: () => void
}

// Inert default so the send path is safe without a provider: nothing to
// register against, collecting yields no extra fields, and notifying is a no-op.
const DEFAULT_VALUE: InvocationContextValue = {
  register: () => () => {},
  collect: () => ({}),
  registerPersistListener: () => () => {},
  notifyPersisted: () => {},
}

const InvocationContext = createContext<InvocationContextValue>(DEFAULT_VALUE)

/**
 * Provider that holds the set of invocation-context contributors and exposes
 * register/collect. Mounted once around the chat tree; agent-specific providers
 * nested inside it register their contributors.
 *
 * @param props.children - The subtree that may register contributors and send.
 * @returns The provider element.
 */
export function InvocationContextProvider({
  children,
}: {
  children: ReactNode
}): React.JSX.Element {
  // A stable set of contributor functions. Held in a ref (not state) because
  // registration must not trigger re-renders and collect() reads it lazily at
  // send time.
  const contributorsRef = useRef<Set<InvocationContextContributor>>(
    new Set<InvocationContextContributor>()
  )

  // Stable set of persist listeners, held in a ref for the same reason as
  // contributors: registration must not re-render, and notifyPersisted() reads
  // the current set lazily when the session is persisted.
  const persistListenersRef = useRef<Set<PersistListener>>(
    new Set<PersistListener>()
  )

  const register = useCallback(
    (contributor: InvocationContextContributor): (() => void) => {
      contributorsRef.current.add(contributor)
      return () => {
        contributorsRef.current.delete(contributor)
      }
    },
    []
  )

  const collect = useCallback((): Record<string, unknown> => {
    const merged: Record<string, unknown> = {}
    for (const contributor of contributorsRef.current) {
      Object.assign(merged, contributor())
    }
    return merged
  }, [])

  const registerPersistListener = useCallback(
    (listener: PersistListener): (() => void) => {
      persistListenersRef.current.add(listener)
      return () => {
        persistListenersRef.current.delete(listener)
      }
    },
    []
  )

  const notifyPersisted = useCallback((): void => {
    for (const listener of persistListenersRef.current) {
      // Isolate listeners: one throwing must not stop the others or the send.
      try {
        listener()
      } catch (err) {
        console.warn("A persist listener threw (ignored):", err)
      }
    }
  }, [])

  const value = useMemo<InvocationContextValue>(
    () => ({ register, collect, registerPersistListener, notifyPersisted }),
    [register, collect, registerPersistListener, notifyPersisted]
  )

  return (
    <InvocationContext.Provider value={value}>{children}</InvocationContext.Provider>
  )
}

/**
 * Hook for the send path to collect extra invocation-body fields, and for
 * agent-specific providers to register a contributor.
 *
 * @returns The current {@link InvocationContextValue}.
 */
export function useInvocationContext(): InvocationContextValue {
  return useContext(InvocationContext)
}
